import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { toastr } from 'react-redux-toastr';
import { connect } from 'react-redux';

import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';

import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import Modal from '../../../common/modal/Modal';

import { ITINERARY_SUPPLIER_FOOD_PATH } from '../../../../config/paths';

import ItinerarySupplierFoodForm from './ItinerarySupplierFoodForm';

import ItinerarySupplierFoodBasicInformationResource from './resource/ItinerarySupplierFoodBasicInformationResource';

import {
  postItinerarySupplierFood,
  getItinerarySupplierFood,
  clearItinerarySupplierFood,
} from '../../../../actions/food/ItinerarySupplierFood';
import { tzNormalizeDate } from '../../../../utils/date';

const NewItinerarySupplierFood = ({
  breadcrumbs,
  dispatchPostItinerarySupplierFood,
  dispatchGetItinerarySupplierFood,
  dispatchClearItinerarySupplierFood,
  itinerarySupplierFood,
  loadingItinerarySupplierFood,
}) => {
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [loadingSavedData, setLoadingSavedData] = useState(false);

  const handleCloseModal = () => {
    setShowSavedModal(false);
    dispatchClearItinerarySupplierFood();
  };

  const onSubmit = async (
    formValues,
    selectedItinerary,
    selectedSupplier,
    registeredBus,
    passengerFlowForItinerary,
  ) => {
    if (!selectedItinerary) {
      toastr.error('Debe seleccionar un itinerario.');
      return null;
    }

    if (!selectedSupplier) {
      toastr.error('Debe buscar un proveedor.');
      return null;
    }

    if (!registeredBus) {
      toastr.error('El itinerario seleccionado no tiene bus registrado.');
      return null;
    }

    const newFormValues = {
      ...formValues,

      supplierId: selectedSupplier,

      itineraryId: selectedItinerary.itineraryId,

      registeredBusId: registeredBus.id,

      scheduleDeliveredDate: selectedItinerary.scheduleDeliveredDate
        ? tzNormalizeDate({
            date: selectedItinerary.scheduleDeliveredDate,
          })
        : null,

      cabinCrewId: formValues.cabinCrewId ? formValues.cabinCrewId.value : null,

      itinerarySupplierFoodDetailList: formValues.supplierFoodTypeList
        ? formValues.supplierFoodTypeList.map((item) => ({
            foodId: item.food.value,
            quantity: item.quantity,
            publicType: item.publicType.value,
            cost: Number(item.quantity) * Number(item.unitCost),
          }))
        : [],

      itinerarySupplierFoodPassengerFlowList: passengerFlowForItinerary
        ? [
            {
              passengerFlow: JSON.stringify(passengerFlowForItinerary),
            },
          ]
        : [],

      comment: formValues.comment,
    };

    const saved = await dispatchPostItinerarySupplierFood(newFormValues);

    if (!saved || !saved.id) {
      return null;
    }

    dispatchClearItinerarySupplierFood();

    setShowSavedModal(true);
    setLoadingSavedData(true);

    try {
      await dispatchGetItinerarySupplierFood({
        itinerarySupplierFoodId: saved.id,
      });
    } finally {
      setLoadingSavedData(false);
    }
    return saved;
  };

  let modalBody = null;

  if (loadingSavedData || loadingItinerarySupplierFood) {
    modalBody = (
      <div className="text-center p-4">
        <Loader />
      </div>
    );
  } else if (!itinerarySupplierFood || itinerarySupplierFood.isEmpty()) {
    modalBody = (
      <div className="alert alert-warning mb-0">
        No se pudo obtener la información del registro guardado.
      </div>
    );
  } else {
    const itinerarySupplierFoodJson = itinerarySupplierFood.toJS();

    modalBody = (
      <>
        <div className="alert alert-success">
          La alimentación por itinerario fue registrada correctamente.
        </div>

        <h4>Información registrada</h4>

        <ItinerarySupplierFoodBasicInformationResource
          {...itinerarySupplierFoodJson}
        />
      </>
    );
  }

  const content = (
    <>
      <ItinerarySupplierFoodForm onSubmit={onSubmit} />

      <Modal
        show={showSavedModal}
        title="Alimentación registrada"
        body={modalBody}
        onClickClose={handleCloseModal}
        onClickConfirm={handleCloseModal}
        confirmButtonText="Cerrar"
        disableConfirmButton={loadingSavedData || loadingItinerarySupplierFood}
        size="xl"
      />
    </>
  );

  return <Content breadcrumbs={breadcrumbs} content={content} />;
};

NewItinerarySupplierFood.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,

  dispatchPostItinerarySupplierFood: PropTypes.func.isRequired,

  dispatchGetItinerarySupplierFood: PropTypes.func.isRequired,

  dispatchClearItinerarySupplierFood: PropTypes.func.isRequired,

  itinerarySupplierFood: PropTypes.instanceOf(Immutable.Map).isRequired,

  loadingItinerarySupplierFood: PropTypes.bool,
};

NewItinerarySupplierFood.defaultProps = {
  loadingItinerarySupplierFood: false,
};

const mapDispatchToProps = {
  dispatchPostItinerarySupplierFood: postItinerarySupplierFood,

  dispatchGetItinerarySupplierFood: getItinerarySupplierFood,

  dispatchClearItinerarySupplierFood: clearItinerarySupplierFood,
};

const mapStateToProps = ({ FoodUnit }) => ({
  breadcrumbs: [
    ...FoodUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Lista de Alimentaciones por Proveedor',
      href: ITINERARY_SUPPLIER_FOOD_PATH,
    },
    {
      text: 'Nuevo',
      href: '',
    },
  ],

  itinerarySupplierFood: FoodUnit.ItinerarySupplierFood.getIn([
    'current',
    'content',
  ]),

  loadingItinerarySupplierFood: !FoodUnit.ItinerarySupplierFood.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(NewItinerarySupplierFood);
